import { defineStore } from "pinia";
import { ref, computed, watch } from "vue";
import { PA_COLUMNS, INTAKE_COLUMNS } from "@/constants/tableColumns";
import { WORKLIST_COLUMNS } from "@/constants/worklistColumns";
import { SCOPE, isTableScope, toBackendScope, CLIENT_CONFIG_SECTION } from "@/constants/scope";
import { settingsService } from "@/services/settingsService";
import { useAuthStore } from "@/stores/authStore";
import { defaultPrefs, mergeWithDefaults, resolveColumns } from "@/utils/tableColumnPrefs";

// Column layouts are persisted per user on the server (LAM-4742) so they follow the
// user across browsers and devices. localStorage is kept only as a per-user cache,
// keyed by user id, so the grid paints the right layout before the fetch resolves.
//
// The pre-LAM-4742 keys were unnamespaced, so everyone sharing a workstation shared
// one layout. They are never read: a shared layout can't be attributed to anyone, and
// adopting it would write a colleague's columns into this user's account and push them
// to every device they use. Such users simply start from defaults, which already order
// Assigned after Status. The old keys are also left in place, so a frontend rollback
// restores each user's pre-upgrade layout rather than resetting everyone.
const LS_BASE_PA = "lh_table_columns_pa";
const LS_BASE_INTAKE = "lh_table_columns_intake";
const LS_BASE_WORKLIST = "lh_table_columns_worklist";
const LS_KEY_PA_EXPANDED = "lh_table_expanded_pa";

const SAVE_DEBOUNCE_MS = 500;

// Base names only — the real cache key always carries the user id (see _cacheKey).
// The bare names are the pre-LAM-4742 shared keys and are deliberately never read.
const LS_BASE_FOR = {
  [SCOPE.PA]: LS_BASE_PA,
  [SCOPE.INTAKE]: LS_BASE_INTAKE,
  [SCOPE.WORKLIST]: LS_BASE_WORKLIST,
};

const COLUMNS_FOR = {
  [SCOPE.PA]: PA_COLUMNS,
  [SCOPE.INTAKE]: INTAKE_COLUMNS,
  [SCOPE.WORKLIST]: WORKLIST_COLUMNS,
};

const TABLE_SCOPES = [SCOPE.PA, SCOPE.INTAKE, SCOPE.WORKLIST];

function readJson(lsKey) {
  try {
    const raw = localStorage.getItem(lsKey);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export const useTableSettingsStore = defineStore("tableSettings", () => {
  const isDialogOpen = ref(false);
  // Scope toggle shared across Columns / Tags / Action Needed tabs in the Settings dialog.
  // Values come from @/constants/scope (SCOPE.PA | SCOPE.INTAKE). Seeded by the caller of open().
  const scope = ref(SCOPE.PA);

  function _currentUserId() {
    try {
      return useAuthStore().userInfo?.pk ?? null;
    } catch {
      return null;
    }
  }

  // Null until we know who the user is. There is deliberately NO fallback to the
  // unnamespaced pre-LAM-4742 key: App.vue instantiates this store at app boot on
  // /login (pk still null) and login is a router.push with no reload, so that
  // fallback would seed the session from whichever colleague last used this
  // workstation — the very leak this ticket removes.
  function _cacheKey(tableKey) {
    const userId = _currentUserId();
    return userId == null ? null : `${LS_BASE_FOR[tableKey]}:u${userId}`;
  }

  function _readCache(tableKey) {
    const key = _cacheKey(tableKey);
    return key ? readJson(key) : null;
  }

  const paColumnPrefs = ref(mergeWithDefaults(_readCache(SCOPE.PA), PA_COLUMNS));
  const intakeColumnPrefs = ref(mergeWithDefaults(_readCache(SCOPE.INTAKE), INTAKE_COLUMNS));
  const worklistColumnPrefs = ref(mergeWithDefaults(_readCache(SCOPE.WORKLIST), WORKLIST_COLUMNS));
  const loaded = ref(false);
  const loadFailed = ref(false);
  const saveError = ref(null);

  // Scopes the user has edited this session. Hydration must not overwrite them, so a
  // slow GET landing after a drag can't resurrect the pre-drag layout.
  const dirtyScopes = new Set();
  const saveTimers = {};
  const savePromises = {};
  let seededForUserId = _currentUserId();
  let inFlight = null;
  let loadGen = 0;

  const paColumns = computed(() => resolveColumns(paColumnPrefs.value, PA_COLUMNS));
  const intakeColumns = computed(() => resolveColumns(intakeColumnPrefs.value, INTAKE_COLUMNS));
  const worklistColumns = computed(() => resolveColumns(worklistColumnPrefs.value, WORKLIST_COLUMNS));

  // Editing is held until hydration settles: on a browser with no cache the grid shows
  // defaults while the GET is in flight, and an edit made against that baseline would
  // be PUT over the user's real saved layout. A failed fetch counts as settled so a
  // dead endpoint can't lock the user out of their own settings.
  const canEditColumns = computed(() => loaded.value || loadFailed.value);

  function _prefsRef(tableKey) {
    return { [SCOPE.PA]: paColumnPrefs, [SCOPE.INTAKE]: intakeColumnPrefs, [SCOPE.WORKLIST]: worklistColumnPrefs }[
      tableKey
    ];
  }

  function _writeCache(tableKey, prefs) {
    const key = _cacheKey(tableKey);
    if (!key) return;
    try {
      localStorage.setItem(key, JSON.stringify(prefs));
    } catch {
      // A full or unavailable localStorage must not break the grid; the server is
      // the source of truth and the next load refetches.
    }
  }

  function _cancelTimer(tableKey) {
    if (!saveTimers[tableKey]) return;
    clearTimeout(saveTimers[tableKey].id);
    saveTimers[tableKey] = null;
  }

  // The store outlives login (router.push, no reload) and a user switch, so re-seed
  // from the now-correct per-user cache whenever the identity changes, and drop every
  // piece of state still scoped to the previous identity: a pending PUT would write
  // their layout into this user's row under this user's auth header, and an in-flight
  // GET would commit their layout into this user's prefs and cache.
  function _reseedIfUserChanged() {
    const userId = _currentUserId();
    if (userId === seededForUserId) return;
    seededForUserId = userId;
    for (const tableKey of TABLE_SCOPES) _cancelTimer(tableKey);
    loadGen++;
    inFlight = null;
    dirtyScopes.clear();
    loaded.value = false;
    loadFailed.value = false;
    for (const tableKey of TABLE_SCOPES) {
      _prefsRef(tableKey).value = mergeWithDefaults(_readCache(tableKey), COLUMNS_FOR[tableKey]);
    }
  }

  function _applyLocal(tableKey, saved) {
    const merged = mergeWithDefaults(saved, COLUMNS_FOR[tableKey]);
    _prefsRef(tableKey).value = merged;
    return merged;
  }

  function _flushSave(tableKey, forUserId) {
    // The identity can change between scheduling and flushing. The PUT would go out
    // under the new user's auth header and write the previous user's layout into their
    // row, so drop it rather than send it to the wrong account.
    if (forUserId !== _currentUserId()) return Promise.resolve();
    const prefs = _prefsRef(tableKey).value;
    const promise = settingsService
      .updateMyTableColumns(toBackendScope(tableKey), prefs)
      .then(() => {
        saveError.value = null;
      })
      .catch((error) => {
        // Keep the local layout and cache so the session and this browser stay correct;
        // the next edit retries. Surfaced so the settings dialog can say it didn't sync.
        saveError.value = error?.message || "Could not save your column layout";
      })
      .finally(() => {
        if (savePromises[tableKey] === promise) savePromises[tableKey] = null;
      });
    // Tracked so a reset can wait for an already-dispatched PUT and can't be overtaken
    // by it on the wire, which would leave the server holding the pre-reset layout.
    savePromises[tableKey] = promise;
    return promise;
  }

  function _scheduleSave(tableKey) {
    const forUserId = _currentUserId();
    dirtyScopes.add(tableKey);
    _writeCache(tableKey, _prefsRef(tableKey).value);
    _cancelTimer(tableKey);
    saveTimers[tableKey] = {
      forUserId,
      id: setTimeout(() => {
        saveTimers[tableKey] = null;
        _flushSave(tableKey, forUserId);
      }, SAVE_DEBOUNCE_MS),
    };
  }

  async function ensureLoaded() {
    _reseedIfUserChanged();
    if (loaded.value) return;
    if (inFlight) return inFlight;

    const gen = ++loadGen;
    inFlight = settingsService
      .getMyTableColumns()
      .then(async (data) => {
        if (gen !== loadGen) return;
        for (const tableKey of TABLE_SCOPES) {
          if (dirtyScopes.has(tableKey)) continue;
          const serverLayout = data?.[CLIENT_CONFIG_SECTION[tableKey]];
          if (Array.isArray(serverLayout)) {
            _writeCache(tableKey, _applyLocal(tableKey, serverLayout));
            continue;
          }
          // Nothing saved server-side. Adopt only this user's own cache — never the
          // shared pre-LAM-4742 key, which cannot be attributed to anyone and would
          // otherwise be written into this user's account and pushed to every device
          // they use. Users with only a shared-key layout start from defaults, which
          // already order Assigned after Status.
          const own = _readCache(tableKey);
          _writeCache(tableKey, _applyLocal(tableKey, own));
          if (own) await _flushSave(tableKey);
        }
        loaded.value = true;
        loadFailed.value = false;
        // The old shared keys are deliberately left in place: nothing reads them any
        // more, and keeping them means a frontend rollback restores each user's
        // pre-upgrade layout instead of resetting everyone to defaults.
      })
      .catch(() => {
        // Offline, or the endpoint is missing mid-rolling-deploy. Keep the cached or
        // default layout and let the next ensureLoaded() retry. `loadFailed` unblocks
        // editing so a failed fetch can't lock the user out of their own settings.
        loadFailed.value = true;
      })
      .finally(() => {
        if (gen === loadGen) inFlight = null;
      });

    return inFlight;
  }

  async function reload() {
    loaded.value = false;
    inFlight = null;
    dirtyScopes.clear();
    return ensureLoaded();
  }

  function open(initialScope) {
    if (isTableScope(initialScope)) {
      scope.value = initialScope;
    }
    isDialogOpen.value = true;
  }

  function close() {
    isDialogOpen.value = false;
  }

  function flushPendingSaves() {
    return Promise.all(
      Object.keys(saveTimers).map((tableKey) => {
        const pending = saveTimers[tableKey];
        if (!pending) return null;
        _cancelTimer(tableKey);
        return _flushSave(tableKey, pending.forUserId);
      })
    );
  }

  function setScope(s) {
    if (isTableScope(s)) scope.value = s;
  }

  function setColumnVisibility(tableKey, colKey, visible) {
    const prefs = _prefsRef(tableKey);
    const item = prefs.value.find((p) => p.key === colKey);
    if (!item) return;
    item.visible = visible;
    _scheduleSave(tableKey);
  }

  function setColumnOrder(tableKey, orderedKeys) {
    const prefs = _prefsRef(tableKey);
    const byKey = new Map(prefs.value.map((p) => [p.key, p]));
    const reordered = orderedKeys.map((key) => byKey.get(key)).filter(Boolean);
    // Anything the caller omitted keeps its relative order at the end, so a stale key
    // list can never silently drop a column out of the layout.
    for (const pref of prefs.value) {
      if (!orderedKeys.includes(pref.key)) reordered.push(pref);
    }
    prefs.value = reordered.map((pref, order) => ({ ...pref, order }));
    _scheduleSave(tableKey);
  }

  async function resetColumns(tableKey) {
    _cancelTimer(tableKey);
    dirtyScopes.delete(tableKey);
    _prefsRef(tableKey).value = defaultPrefs(COLUMNS_FOR[tableKey]);
    const key = _cacheKey(tableKey);
    if (key) {
      try {
        localStorage.removeItem(key);
      } catch {
        // A blocked localStorage still leaves the server as the source of truth.
      }
    }
    try {
      // Let any already-dispatched save land first, otherwise a PUT that overtakes the
      // DELETE leaves the server holding the layout the user just reset.
      await savePromises[tableKey];
      await settingsService.resetMyTableColumns(toBackendScope(tableKey));
      saveError.value = null;
    } catch (error) {
      saveError.value = error?.message || "Could not reset your column layout";
    }
  }

  const paExpandedView = ref(localStorage.getItem(LS_KEY_PA_EXPANDED) === "true");

  function togglePAExpandedView() {
    paExpandedView.value = !paExpandedView.value;
    localStorage.setItem(LS_KEY_PA_EXPANDED, String(paExpandedView.value));
  }

  function setPAExpandedView(val) {
    paExpandedView.value = val;
    localStorage.setItem(LS_KEY_PA_EXPANDED, String(val));
  }

  // Watched rather than handled in open()/close() because the dialog is bound with
  // v-model, so it opens and closes (escape, backdrop, the X) without calling either.
  // On open: hydrate, since Settings is reachable from the sidebar on pages that never
  // mount a grid. On close: send anything still in the debounce window straight away,
  // so navigating off right after an edit can't lose it.
  watch(isDialogOpen, (open) => {
    if (open) ensureLoaded();
    else flushPendingSaves();
  });

  return {
    isDialogOpen,
    scope,
    paColumnPrefs,
    intakeColumnPrefs,
    worklistColumnPrefs,
    paColumns,
    intakeColumns,
    worklistColumns,
    loaded,
    loadFailed,
    canEditColumns,
    saveError,
    ensureLoaded,
    reload,
    flushPendingSaves,
    open,
    close,
    setScope,
    paExpandedView,
    togglePAExpandedView,
    setPAExpandedView,
    setColumnVisibility,
    setColumnOrder,
    resetColumns,
  };
});
